import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import type { IconType } from 'react-icons';
import {
  FaBox,
  FaCheck,
  FaChevronRight,
  FaFileAlt,
  FaPoundSign,
  FaTruck,
  FaWrench,
} from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import type { WorkflowTask } from './ProjectWorkflow';
import type { Project } from '../types/project';
import { EVENT_LINKED_TASKS } from '../pages/project-workspace/types';
import { applyRecordTicks, checklistByStage } from '../pages/project-workspace/checklistRecords';
import { workspaceStageLabel } from '../pages/project-workspace/workspaceStages';
import type { WorkspaceSnapshot } from '../pages/project-workspace/workspaceDashboard.model';
import type { OperationalQueueId } from '../pages/project-workspace/operationalState';
import { directSupabase } from '../lib/supabaseClient';

interface ProjectLifecycleBarProps {
  project: Project;
  tasks: WorkflowTask[];
  expandedStage: OperationalQueueId | null;
  onExpandStage: (stage: OperationalQueueId | null) => void;
  /** Where the job is (same answer as the Board) and which steps records have ticked. */
  snapshot?: WorkspaceSnapshot | null;
  onTaskChange: (taskName: string, completed: boolean) => void;
  eventsRefreshKey?: number;
  saving?: boolean;
}

function parseLinkedEventDate(dateStr: string) {
  if (dateStr && !dateStr.match(/(Z|[+-][0-9]{2}:?[0-9]{2})$/)) {
    return new Date(`${dateStr}Z`);
  }
  return new Date(dateStr);
}

function formatLinkedEventLabel(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const date = parseLinkedEventDate(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
  });
  const start = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/London',
  });
  if (start === '00:00') return day;
  const end = new Date(date.getTime() + 60 * 60 * 1000);
  const endLabel = end.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/London',
  });
  return `${day} · ${start} – ${endLabel}`;
}

const STAGE_ICONS: Record<OperationalQueueId, IconType> = {
  presale: FaFileAlt,
  deposit: FaPoundSign,
  ordering: FaBox,
  installation: FaWrench,
  aftercare: FaCheck,
};

const ProjectLifecycleBar: React.FC<ProjectLifecycleBarProps> = ({
  project,
  tasks,
  expandedStage,
  onExpandStage,
  onTaskChange,
  eventsRefreshKey = 0,
  saving = false,
  snapshot = null,
}) => {
  const supplyOnly = !!project.supply_only;
  const [linkedEventDates, setLinkedEventDates] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const types = Object.values(EVENT_LINKED_TASKS);
      const { data } = await directSupabase
        .from('project_events')
        .select('event_type, event_date, status, created_at')
        .eq('project_id', project.id)
        .in('event_type', types)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      const latest: Record<string, { event_date: string | null; created_at?: string | null }> = {};
      (data || []).forEach((row: { event_type: string; event_date: string | null; status: string | null; created_at?: string | null }) => {
        if (row.status === 'cancelled') return;
        const current = latest[row.event_type];
        const rowDated = row.event_date ? 1 : 0;
        const currentDated = current?.event_date ? 1 : 0;
        if (!current || rowDated > currentDated) {
          latest[row.event_type] = row;
          return;
        }
        if (rowDated === currentDated) {
          const rowMs = Date.parse(row.event_date || row.created_at || '') || 0;
          const currentMs = Date.parse(current.event_date || current.created_at || '') || 0;
          if (rowMs > currentMs) latest[row.event_type] = row;
        }
      });
      const labels: Record<string, string> = {};
      Object.entries(EVENT_LINKED_TASKS).forEach(([taskName, eventType]) => {
        const label = formatLinkedEventLabel(latest[eventType]?.event_date);
        if (label) labels[taskName] = label;
      });
      setLinkedEventDates(labels);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [project.id, eventsRefreshKey]);
  // Steps backed by a record (paid invoice, booking) are ticked from it and can't be unticked here
  const recordTicks = snapshot?.recordTicks || {};
  const effectiveTasks = applyRecordTicks(tasks, recordTicks);
  const isTicked = (name: string) => !!effectiveTasks.find((task) => task.task === name)?.completed;
  const groups = checklistByStage(supplyOnly);
  const cancelled = snapshot?.stage === 'cancelled' || isTicked('Cancel Project');
  const completed = !cancelled && (snapshot?.stage === 'completed' || isTicked('Complete Project'));
  const currentStage = snapshot && !cancelled && !completed ? snapshot.stage : null;

  const tanks = groups.map(({ stage, tasks: names }) => {
    const done = names.filter(isTicked).length;
    const total = names.length;
    const pct = total === 0 ? 0 : Math.round((done / total) * 100);
    return { stage, done, total, pct };
  });

  const expandedGroup = groups.find((group) => group.stage === expandedStage);
  const expandedTasks = expandedGroup
    ? [...expandedGroup.tasks, ...(expandedGroup.stage === 'aftercare' ? ['Cancel Project'] : [])]
    : [];
  const doneCount = tanks.reduce((sum, tank) => sum + tank.done, 0);
  const totalCount = tanks.reduce((sum, tank) => sum + tank.total, 0);
  const overallPct = totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'lg',
        bgcolor: 'background.surface',
        p: { xs: 1.5, md: 2 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ mb: 1.5 }} flexWrap="wrap">
        <Box>
          <Typography level="title-md">Project lifecycle</Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
            {snapshot?.state
              ? `${workspaceStageLabel(snapshot.stage)} · ${snapshot.stateLabel}`
              : 'Track progress from enquiry to completion'}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" sx={{ minWidth: 180, flex: { xs: '1 1 180px', sm: '0 1 240px' } }}>
          <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 0.5 }}>
            {doneCount} of {totalCount} tasks completed
          </Typography>
          <LinearProgress
            determinate
            value={overallPct}
            sx={{ width: '100%', height: 6, '--LinearProgress-radius': '6px' }}
          />
        </Stack>
      </Stack>

      {cancelled && (
        <Chip size="sm" color="danger" variant="soft" sx={{ mb: 1.5 }}>Cancelled</Chip>
      )}
      {completed && (
        <Chip size="sm" color="success" variant="soft" sx={{ mb: 1.5 }}>Completed</Chip>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 0.75,
          overflowX: 'auto',
          pb: 0.5,
        }}
      >
        {tanks.map((tank, index) => {
          const selected = expandedStage === tank.stage;
          const current = !cancelled && currentStage === tank.stage;
          // Full selected chrome only for the open card — or the workflow current
          // stage when nothing is expanded. Never paint both as selected at once.
          const showSelectedChrome = selected || (current && !expandedStage);
          const currentMarker = current && !selected && !!expandedStage;
          const icon = supplyOnly && tank.stage === 'installation' ? FaTruck : STAGE_ICONS[tank.stage];
          return (
            <React.Fragment key={tank.stage}>
              {index > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'neutral.300', flexShrink: 0, px: 0.25 }}>
                  <IconWrapper icon={FaChevronRight} />
                </Box>
              )}
              <Box
                onClick={() => onExpandStage(selected ? null : tank.stage)}
                aria-current={current ? 'step' : undefined}
                aria-expanded={selected}
                sx={{
                  flex: '1 1 0',
                  minWidth: 148,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: showSelectedChrome
                    ? 'var(--jarvis-stage-selected-border)'
                    : currentMarker
                      ? 'var(--jarvis-hover-border)'
                      : 'var(--jarvis-stage-border)',
                  borderRadius: 'lg',
                  p: 1.25,
                  bgcolor: showSelectedChrome ? 'var(--jarvis-stage-selected-bg)' : 'var(--jarvis-stage-bg)',
                  color: showSelectedChrome ? 'var(--jarvis-stage-selected-text)' : 'var(--jarvis-stage-text)',
                  boxShadow: currentMarker ? 'inset 0 0 0 1px var(--jarvis-hover-border)' : 'none',
                  opacity: cancelled ? 0.7 : 1,
                  transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease',
                  '&:hover': {
                    bgcolor: 'var(--jarvis-hover-bg)',
                    borderColor: 'var(--jarvis-hover-border)',
                    color: 'var(--jarvis-hover-text)',
                    boxShadow: 'none',
                    '& .lifecycle-tank-icon': {
                      bgcolor: 'var(--jarvis-accent)',
                      color: 'var(--jarvis-hover-text)',
                    },
                    '& .lifecycle-tank-meta': {
                      color: 'var(--jarvis-stage-meta-hover-text)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <Box
                    className="lifecycle-tank-icon"
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: showSelectedChrome ? 'var(--jarvis-stage-selected-badge-bg)' : 'var(--jarvis-stage-badge-bg)',
                      color: showSelectedChrome ? 'var(--jarvis-stage-selected-badge-text)' : 'var(--jarvis-stage-badge-text)',
                      transition: 'background-color 120ms ease, color 120ms ease',
                    }}
                  >
                    <IconWrapper icon={icon} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-sm" fontWeight="lg" sx={{ lineHeight: 1.2, color: 'inherit' }}>
                      {workspaceStageLabel(tank.stage)}
                    </Typography>
                    <Typography
                      className="lifecycle-tank-meta"
                      level="body-xs"
                      sx={{
                        mt: 0.15,
                        color: showSelectedChrome
                          ? 'var(--jarvis-stage-selected-meta-text)'
                          : 'var(--jarvis-stage-meta-text)',
                        transition: 'color 120ms ease',
                      }}
                    >
                      {tank.done} / {tank.total}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>

      {expandedStage && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.level1', borderRadius: 'md' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography level="title-sm">
              {workspaceStageLabel(expandedStage)}
            </Typography>
            <Button size="sm" variant="plain" onClick={() => onExpandStage(null)}>
              Collapse
            </Button>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <Stack gap={1}>
            {expandedTasks.map((taskName) => {
              const fromRecord = recordTicks[taskName];
              const eventDateLabel = linkedEventDates[taskName];
              return (
                <Box key={taskName}>
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    {fromRecord ? (
                      <Tooltip title="Ticked from the record; it updates itself" variant="soft" placement="top-start">
                        <span>
                          <Checkbox checked disabled label={taskName} />
                        </span>
                      </Tooltip>
                    ) : (
                      <Checkbox
                        checked={isTicked(taskName)}
                        disabled={saving}
                        label={taskName}
                        onChange={(event) => onTaskChange(taskName, event.target.checked)}
                      />
                    )}
                    {fromRecord && (
                      <Chip size="sm" color="success" variant="soft">
                        {fromRecord}
                      </Chip>
                    )}
                    {eventDateLabel && (
                      <Chip size="sm" color="primary" variant="soft">
                        {eventDateLabel}
                      </Chip>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ProjectLifecycleBar;
